import moment from 'moment';
import {alpha} from '@material-ui/core/styles';
import {getCategoryAxisGuideColor} from './graphHelpers';
import {buildSettingCountsByDate} from './panelChangeSettingKey';

export const PANEL_CHANGE_GUIDE_PREFIX = 'panel-change-';

export const sanitizeGuideDateId = date => String(date).replace(/[^a-zA-Z0-9]/g, '-');

export const formatPanelChangeCount = count => (count > 1000 ? '1000+' : String(count));

export const getPanelChangeBadgeBackground = theme => alpha(theme.palette.background.default, 0.9);

export const getPanelChangeBadgeBackgroundHover = theme =>
  alpha(theme.palette.background.default, 1);

export const formatPanelChangeDetailTitle = (dateKey, period) => {
  const m = moment(dateKey);
  if (!m.isValid()) return 'Change log';

  if (period?.label === 'realtime' || period?.label === 'Last 24 hours' || period?.label === 'Today') {
    return m.format('MMM D, HH:mm');
  }

  return m.format('MMM D, YYYY');
};

export const formatPanelChangeBadgeTooltip = (count, dateKey, period) => {
  const dateLabel = formatPanelChangeDetailTitle(dateKey, period);
  const settingWord = count === 1 ? 'setting' : 'settings';
  return `${count} ${settingWord} changed on ${dateLabel} — click to view`;
};

export const formatSspTypeLabel = value => {
  if (value === '0' || value === 0) return 'Direct';
  if (value === '1' || value === 1) return 'Reseller';
  return '—';
};

/** Multiline hover copy for Prebid SSP Direct/Reseller flip markers (no click). */
export const formatSspTypeChangeBadgeTooltip = (changes, dateKey, period) => {
  const dateLabel = formatPanelChangeDetailTitle(dateKey, period);
  const list = Array.isArray(changes) ? changes : [];
  const header = `${list.length || 0} SSP change${
    list.length === 1 ? '' : 's'
  } on ${dateLabel}`;

  if (!list.length) return header;

  const lines = list.map(change => {
    const code = change.sspCode || 'ssp';
    const name = change.alias ? `${code} (${change.alias})` : code;
    return `${name}: → ${formatSspTypeLabel(change.newValue)}`;
  });

  return [header, ...lines].join('\n');
};

const resolveSettingCountsByDate = panelChanges =>
  panelChanges?.changesByDate
    ? buildSettingCountsByDate(panelChanges.changesByDate)
    : panelChanges?.countsByDate || {};

export const buildPanelChangeGuides = (panelChanges, theme) => {
  if (!panelChanges?.countsByDate) return [];

  // Guides are used ONLY as the visual vertical line. The count + click target
  // are rendered as an HTML overlay (see PanelChangeGuideStrips) because reading
  // rendered guide geometry is far more reliable than AmCharts category->pixel math.
  return Object.entries(panelChanges.countsByDate).map(([date]) => ({
    id: `${PANEL_CHANGE_GUIDE_PREFIX}${sanitizeGuideDateId(date)}`,
    panelChangeGuide: true,
    category: date,
    date,
    lineColor: getCategoryAxisGuideColor(theme),
    lineAlpha: 1,
    dashLength: 3,
    lineThickness: 2,
    // Single space keeps an (invisible) label node around as a reliable
    // X-position fallback when guide.line.node isn't readable.
    label: ' ',
    inside: true,
    position: 'bottom',
  }));
};

const getGuideDate = guide => guide?.category || guide?.date;

const resolvePanelDateKey = (guide, panelDates) => {
  const raw = getGuideDate(guide);
  if (raw == null) return null;

  const candidates =
    raw instanceof Date
      ? [
          moment(raw).format('YYYY-MM-DD HH:mm:ss'),
          moment(raw).startOf('hour').format('YYYY-MM-DD HH:mm:ss'),
          moment(raw).format('YYYY-MM-DD'),
        ]
      : [String(raw), String(raw).slice(0, 10)];

  return candidates.find(candidate => panelDates.has(candidate)) || null;
};

const isPanelChangeGuide = (guide, panelDates) => {
  if (guide?.toDate) return false;
  return Boolean(resolvePanelDateKey(guide, panelDates));
};

const getGuideStripWidth = chart => {
  const plotWidth = chart?.plotAreaWidth || 400;
  const pointCount = chart?.dataProvider?.length || 20;
  return Math.min(60, Math.max(26, (plotWidth / pointCount) * 0.9));
};

/**
 * Try to read the rendered guide line node directly off the AmCharts guide
 * object. Works in some builds; we fall back to a DOM scan when it doesn't.
 */
const getGuideNodeRect = guide => {
  const node =
    guide?.line?.node ||
    guide?.set?.node ||
    guide?.fill?.node ||
    (guide?.labelLabel?.node
      ? guide.labelLabel.node.tagName === 'tspan'
        ? guide.labelLabel.node.parentNode
        : guide.labelLabel.node
      : null);
  const rect = node?.getBoundingClientRect?.();
  if (rect && rect.height > 4 && rect.width <= 6) return rect;
  return null;
};

/**
 * Ground truth: scan the chart SVG for the dashed vertical guide lines we
 * rendered (tall, thin, dashed) and return their bounding rects. This avoids
 * relying on AmCharts internal object shape or category->pixel math.
 */
const findVerticalGuideLineRects = chart => {
  const root = chart?.chartDiv;
  if (!root?.querySelectorAll) return [];

  const plotHeight = chart.plotAreaHeight || 0;
  const minHeight = plotHeight > 0 ? plotHeight * 0.5 : 40;
  const rects = [];

  root.querySelectorAll('path, line').forEach(node => {
    if (!node.getAttribute('stroke-dasharray')) return;
    const rect = node.getBoundingClientRect();
    if (rect.width <= 4 && rect.height >= minHeight) {
      rects.push(rect);
    }
  });

  return rects;
};

/** Dates whose category is currently within the visible (possibly zoomed) range. */
const getVisiblePanelDatesSorted = (chart, panelChanges) => {
  const axis = chart?.categoryAxis;
  const provider = chart?.dataProvider || [];
  const allDates = Object.keys(panelChanges.countsByDate).sort();

  if (!axis || provider.length === 0) return allDates;

  const start = axis.startIndex != null ? axis.startIndex : 0;
  const end = axis.endIndex != null ? axis.endIndex : provider.length - 1;

  const inRange = new Set();
  for (let i = start; i <= end && i < provider.length; i += 1) {
    const d = String(provider[i]?.date ?? '');
    if (!d) continue;
    inRange.add(d);
    inRange.add(d.slice(0, 10));
  }

  const visible = allDates.filter(date => inRange.has(date) || inRange.has(date.slice(0, 10)));

  return visible.length > 0 ? visible : allDates;
};

export const computeGuideStripLayouts = (chart, panelChanges, containerEl) => {
  const axis = chart?.categoryAxis;
  if (!axis?.guides || !containerEl || !panelChanges?.countsByDate) return [];

  const containerRect = containerEl.getBoundingClientRect();
  if (containerRect.width <= 0) return [];

  const panelDates = new Set(Object.keys(panelChanges.countsByDate));
  const stripWidth = getGuideStripWidth(chart);

  const settingCounts = resolveSettingCountsByDate(panelChanges);
  const toLayout = (date, rect) => ({
    date,
    count: settingCounts[date] ?? 0,
    left: rect.left + rect.width / 2 - containerRect.left,
    top: rect.top - containerRect.top,
    width: stripWidth,
    height: rect.height,
  });

  // Fast path: rects straight from the guide objects (date-accurate).
  const direct = [];
  axis.guides.forEach(guide => {
    if (!isPanelChangeGuide(guide, panelDates)) return;
    const date = resolvePanelDateKey(guide, panelDates);
    if (!date) return;
    const rect = getGuideNodeRect(guide);
    if (rect) direct.push(toLayout(date, rect));
  });
  if (direct.length > 0) return direct;

  // Fallback: pair DOM-detected dashed vertical lines (sorted by x) with the
  // visible change dates (sorted chronologically). On a time axis x increases
  // with date, so index pairing is correct.
  const lineRects = findVerticalGuideLineRects(chart).sort((a, b) => a.left - b.left);
  if (lineRects.length === 0) return [];

  const visibleDates = getVisiblePanelDatesSorted(chart, panelChanges);
  const n = Math.min(lineRects.length, visibleDates.length);
  const layouts = [];
  for (let i = 0; i < n; i += 1) {
    layouts.push(toLayout(visibleDates[i], lineRects[i]));
  }

  return layouts;
};

/**
 * Visual emphasis only (thicker stroke on the rendered line). Guarded by a
 * signature so it doesn't re-run on every redraw and cause render loops.
 */
export const decoratePanelChangeGuides = (chart, panelChanges, {theme} = {}) => {
  const axis = chart?.categoryAxis;
  if (!axis?.guides || !panelChanges?.countsByDate) return;
  if (chart._panelChangeGuideDecorating) return;

  const zoomKey =
    axis.startIndex != null && axis.endIndex != null
      ? `${axis.startIndex}-${axis.endIndex}`
      : 'all';
  const signature = `${Object.keys(panelChanges.countsByDate).sort().join('|')}:${zoomKey}`;
  if (chart._panelChangeGuideSignature === signature) return;

  chart._panelChangeGuideDecorating = true;
  chart._panelChangeGuideSignature = signature;

  try {
    const panelDates = new Set(Object.keys(panelChanges.countsByDate));
    axis.guides.forEach(guide => {
      if (!isPanelChangeGuide(guide, panelDates)) return;
      if (guide.line?.attr) {
        guide.line.attr({
          'stroke-width': 2,
          'stroke-opacity': 1,
          stroke: getCategoryAxisGuideColor(theme),
        });
      }
    });
  } finally {
    chart._panelChangeGuideDecorating = false;
  }
};
